/**
 * Register the service worker + surface a toast when a new version is available.
 * The user can tap the toast to trigger SKIP_WAITING and reload.
 */
import { toast } from "sonner";

export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production") {
    // Skip in dev — HMR breaks with a SW cache
    return;
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      // If there's a waiting worker on first load, prompt
      if (reg.waiting) promptUpdate(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            promptUpdate(nw);
          }
        });
      });
    }).catch(() => { /* silent */ });

    // Reload once the new SW takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

function promptUpdate(worker) {
  toast("A new version of TradePilot is available.", {
    action: {
      label: "Refresh",
      onClick: () => worker.postMessage({ type: "SKIP_WAITING" }),
    },
    duration: 30000,
    id: "sw-update",
  });
}

/**
 * Simple `beforeinstallprompt` capture — component-level code can query this
 * to render the "Install App" button (see PWAInstallButton).
 */
let deferredInstallPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    window.dispatchEvent(new Event("pwa-installable"));
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    window.dispatchEvent(new Event("pwa-installed"));
  });
}

export function canInstall() {
  return Boolean(deferredInstallPrompt);
}

export async function triggerInstall() {
  if (!deferredInstallPrompt) return false;
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome === "accepted";
}
